import { MapPin, X } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { Logo, LogoMark } from '@/components/brand/logo';
import { Avatar } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  isOperatorShift,
  PRIMARY_NAV,
  QUICK_LINKS,
  SUPPORT_LINKS,
  type ShellLink,
} from '@/config/navigation';
import { useAuthStore } from '@/features/auth/auth.store';
import { ModuleIcon } from '@/features/modulos/components/module-icon';
import { useModulosDisponibles } from '@/features/modulos/hooks/use-modulos-disponibles';
import type { CategoriaModulo } from '@/features/modulos/modulos.types';
import { useApiHealth } from '@/hooks/use-api-health';
import { cn } from '@/lib/utils';

import { SidebarLink, SidebarSection, SidebarShellLink } from './sidebar-nav';
import { SIDEBAR_ICON_ACTIVE, SIDEBAR_ICON_IDLE, sidebarLinkClasses } from './sidebar-styles';

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
  /** Menú contraído a iconos (sólo en escritorio; el cajón de móvil va entero). */
  contraido: boolean;
}

const LINK_BASE = 'group flex items-center gap-3 rounded-lg px-3 transition-colors';

/** Estado de EDSuite API: se muestra al lado de la sucursal, en el pie del menú. */
const ESTADOS_API = {
  checking: { etiqueta: 'Conectando', punto: 'bg-amber-400', tono: 'text-white/55' },
  online: { etiqueta: 'En línea', punto: 'bg-emerald-400', tono: 'text-emerald-300' },
  offline: { etiqueta: 'Sin conexión', punto: 'bg-rose-400', tono: 'text-rose-300' },
} as const;

/**
 * Menú lateral del panel.
 *
 * - **Accesos fijos**: Módulos y Dashboard.
 * - **Categorías**: el catálogo real del usuario (base de datos, ya filtrado por
 *   permisos y plan), así que nunca se ofrece un acceso prohibido.
 * - **Accesos rápidos / Soporte**: enlaces del producto que aún viven en el panel
 *   anterior o apuntan fuera de la aplicación.
 * - **Pie**: tarjeta del usuario con su rol y la sucursal.
 *
 * En escritorio puede ir **contraído** (`contraido`): se queda en una franja de
 * iconos, sin textos, para dejar más sitio al contenido. En móvil siempre se abre
 * entero, porque es un cajón que el usuario abre a propósito.
 */
export function Sidebar({ mobileOpen, onClose, contraido }: SidebarProps) {
  const user = useAuthStore((state) => state.user);
  const { data, isPending } = useModulosDisponibles();
  const { pathname } = useLocation();
  const estadoApi = ESTADOS_API[useApiHealth()];

  const categorias = data?.categorias ?? [];
  const sucursal = data?.empresa?.sucursal ?? null;
  const primaryRole = user?.isAdmin ? 'Administrador' : (user?.groups[0]?.description ?? 'Operador');

  /**
   * ¿La ruta actual pertenece a esta categoría?
   *
   * No basta con que el enlace sea `/inicio/<codigo>`: al entrar a un módulo
   * (p. ej. `/gestion/clientes-app`) la categoría debe seguir marcada para que el
   * usuario sepa en qué sección está. Se compara contra el `web_path` de cada
   * módulo del catálogo.
   */
  const esCategoriaActiva = (categoria: CategoriaModulo): boolean => {
    if (pathname === `/inicio/${categoria.codigo}`) return true;

    return categoria.modulos.some((modulo) => {
      const ruta = modulo.web_path;

      return Boolean(ruta) && (pathname === ruta || pathname.startsWith(`${ruta}/`));
    });
  };

  // El panel anterior oculta atajos y soporte a los operadores de turno.
  const operatorShift = isOperatorShift(user?.groupIds);
  const visibleLinks = (links: readonly ShellLink[]): ShellLink[] =>
    links.filter((link) => !(link.hideForOperatorShift && operatorShift));

  const quickLinks = visibleLinks(QUICK_LINKS);
  const supportLinks = visibleLinks(SUPPORT_LINKS);

  return (
    <>
      {/* Fondo oscurecido en móvil */}
      {mobileOpen ? (
        <div
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-0 z-30 animate-fade-in bg-ink-950/50 backdrop-blur-sm lg:hidden"
        />
      ) : null}

      <aside
        aria-label="Navegación principal"
        data-contraido={contraido ? 'true' : 'false'}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[17.5rem] flex-col bg-ink-950 transition-[transform,width] duration-300 ease-out',
          contraido && 'lg:w-[4.75rem]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-brand-700/35 to-transparent"
        />

        <header className="relative flex items-center justify-between px-5 py-5">
          <Logo
            className="menu-logo-completo"
            tone="light"
            size="sm"
            tagline="Estaciones de servicio"
          />
          <LogoMark className="menu-logo-marca size-7" />

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar menú"
            className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
          >
            <X className="size-5" />
          </button>
        </header>

        <nav className="relative flex-1 overflow-y-auto px-3 pb-4">
          {/* Accesos fijos */}
          <ul className="space-y-0.5">
            {PRIMARY_NAV.map((item) => {
              const Icon = item.icon;

              return (
                <SidebarLink
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  end={item.end}
                  onNavigate={onClose}
                  icon={<Icon className="size-4.5" />}
                  contraido={contraido}
                />
              );
            })}
          </ul>

          {/* Categorías del catálogo (base de datos) */}
          <SidebarSection title="Categorías">
            {isPending ? (
              <div className="space-y-2 px-3 py-1">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-5 bg-white/10" />
                ))}
              </div>

            ) : categorias.length === 0 ? (
              <p className="mx-3 rounded-lg border border-white/10 bg-white/[0.04] p-3 text-xs leading-relaxed text-white/55">
                Sin módulos asignados. Contacta al administrador del sistema.
              </p>
            ) : (
              <ul className="space-y-0.5">
                {categorias.map((categoria) => {
                  const activa = esCategoriaActiva(categoria);

                  return (
                    <li key={categoria.codigo}>
                      <NavLink
                        to={`/inicio/${categoria.codigo}`}
                        onClick={onClose}
                        title={contraido ? categoria.nombre : undefined}
                        className={cn(LINK_BASE, 'py-2 text-sm', sidebarLinkClasses(activa))}
                      >
                        <ModuleIcon
                          name={categoria.icono_web}
                          className={cn('size-4 shrink-0', activa ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_IDLE)}
                        />
                        <span className="menu-texto flex-1 truncate">{categoria.nombre}</span>
                        <span
                          className={cn(
                            'menu-extra shrink-0 rounded-full px-1.5 py-0.5 font-mono text-[10px] tabular-nums',
                            activa ? 'bg-brand-400/20 text-brand-200' : 'bg-white/[0.08] text-white/45',
                          )}
                        >
                          {categoria.total_modulos}
                        </span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </SidebarSection>

          {/* Accesos rápidos */}
          {quickLinks.length > 0 ? (
            <SidebarSection title="Accesos rápidos">
              <ul className="space-y-0.5">
                {quickLinks.map((link) => (
                  <SidebarShellLink
                    key={link.label}
                    link={link}
                    onNavigate={onClose}
                    contraido={contraido}
                  />
                ))}
              </ul>
            </SidebarSection>
          ) : null}

          {/* Soporte */}
          {supportLinks.length > 0 ? (
            <SidebarSection title="Soporte">
              <ul className="space-y-0.5">
                {supportLinks.map((link) => (
                  <SidebarShellLink
                    key={link.label}
                    link={link}
                    onNavigate={onClose}
                    contraido={contraido}
                  />
                ))}
              </ul>
            </SidebarSection>
          ) : null}
        </nav>

        {/* Pie: usuario y sucursal */}
        <footer className="relative border-t border-white/10 p-3">
          <div
            className="menu-pie flex items-center gap-3 rounded-xl bg-white/[0.05] p-3"
            title={contraido ? (user?.displayName ?? 'Usuario') : undefined}
          >
            <Avatar name={user?.displayName ?? 'Usuario'} src={user?.photo} size="sm" className="ring-0" />

            <div className="menu-extra min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{user?.displayName ?? '—'}</p>
              <p className="truncate text-xs text-white/50">{primaryRole}</p>
            </div>
          </div>

          {/* Sucursal + estado del backend: así se sabe si la API responde sin abrir nada. */}
          <div
            title={contraido ? `Servidor: ${estadoApi.etiqueta}` : undefined}
            className="menu-chip mt-2 flex items-center justify-between gap-2 rounded-lg bg-white/[0.05] px-2 py-1 text-[11px] font-medium"
          >
            {sucursal ? (
              <span
                className="menu-extra flex min-w-0 items-center gap-1.5 text-white/60"
                title={sucursal}
              >
                <MapPin className="size-3 shrink-0 text-brand-300" aria-hidden="true" />
                <span className="menu-texto truncate">{sucursal}</span>
              </span>
            ) : null}

            <span
              className={cn('flex shrink-0 items-center gap-1.5', estadoApi.tono)}
              aria-label={`Servidor: ${estadoApi.etiqueta}`}
            >
              <span aria-hidden="true" className={cn('size-1.5 rounded-full', estadoApi.punto)} />
              <span className="menu-texto">{estadoApi.etiqueta}</span>
            </span>
          </div>
        </footer>
      </aside>
    </>
  );
}
